/**
 * The single typed API client for the whole console. Built from @salonomy/api-client (generated
 * from the OpenAPI contract — ADR-0003). Every request/response is typed straight from the spec;
 * we never hand-roll fetch calls (brief WS2 non-negotiable). `credentials: 'include'` carries the
 * first-party session cookie (ADR-0006).
 */
import { createSalonomyClient, type SalonomyClient } from '@salonomy/api-client';
import type { ApiError } from '@salonomy/types';
import { env } from './env.js';

export const api: SalonomyClient = createSalonomyClient({
  baseUrl: env.apiBaseUrl,
  credentials: 'include',
});

/** A failed API call, carrying the server's uniform error envelope (primitives.ts → ApiError). */
export class ApiClientError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(status: number, body: ApiError['error'] | undefined, fallback: string) {
    super(body?.message ?? fallback);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = body?.code ?? 'error';
    this.details = body?.details;
    this.requestId = body?.requestId;
  }

  get isAuth(): boolean {
    return this.status === 401;
  }
  get isConflict(): boolean {
    return this.status === 409;
  }
}

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/** Throw on non-2xx (parsing the error envelope), otherwise return the typed payload. */
export function unwrap<T>(result: FetchResult<T>): T {
  if (!result.response.ok) {
    const env_ = (result.error ?? result.data) as ApiError | undefined;
    throw new ApiClientError(
      result.response.status,
      env_?.error,
      `Request failed (${result.response.status})`,
    );
  }
  return result.data as T;
}

/** 204 responses carry no body; callers that don't need data can await this. */
export function expectOk(result: FetchResult<unknown>): void {
  if (!result.response.ok) {
    const env_ = result.error as ApiError | undefined;
    throw new ApiClientError(
      result.response.status,
      env_?.error,
      `Request failed (${result.response.status})`,
    );
  }
}
